import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTemplates,
  setClickedDocOrFolderIdInStore,
  setOpenCreateDocumentModal,
  setOpenRenameFileModal,
  setTemplateId,
} from '../features/template/template';
import useChangeNavigationOnActiveFile from './useChangeNavigationOnActiveFile';
import { findTemplateById, getGitLinkForBitbucket } from '../utils/common';
import { createFile, updateBitbucketFiles } from '../features/organization/organizationSlice';
import { getTemplateById, trackTemplateUsage } from '../components/Modal/helper/templateHelper';
import jsonToDocusaurusConvertor from 'editor/src/json-to-docusaurus';

/**
 * Custom hook for document creation
 * @returns {Object} Document creation functions
 */
const useDocumentCreation = () => {
  const dispatch = useDispatch();
  const organization = useSelector((state) => state.organization);
  const user = useSelector((state) => state.user);
  const templates = useSelector((state) => state.template.templates);
  const clickedDocOrFolderId = useSelector((state) => state.template?.clickedDocOrFolderId);
  const { changeNavigationOnActiveFile } = useChangeNavigationOnActiveFile();

  /**
   * Open create document modal
   * @param {string} id - ID of the folder to create document in
   */
  const openCreateDocument = useCallback(
    (id) => {
      if (id) {
        dispatch(setClickedDocOrFolderIdInStore(id));
      }
      dispatch(setOpenCreateDocumentModal(true));
    },
    [dispatch]
  );

  const newFileClickHandler = useCallback(
    async (fileData, newFileTitle, clickedDocOrFolderId) => {
      if (organization?.activeWorkspace?.is_git_based) {
        const path = clickedDocOrFolderId
          ? `${clickedDocOrFolderId}/${newFileTitle}.md`
          : `${organization?.activeWorkspace?.meta_info?.bitbucket_directory_name}/${newFileTitle}.md`;

        const body = {
          scm_provider: organization?.activeWorkspace?.scm_provider,
          git_link: getGitLinkForBitbucket(organization?.activeWorkspace?.git_link),
          files: [{ path, raw: fileData }],
          commit_info: {
            commitMessage: `New File ${newFileTitle} Created`,
            parentSha: null,
            branch: organization?.activeWorkspace?.meta_info?.bitbucket_branch_name,
          },
          commit_author: {
            name: user?.username,
            email: user?.email,
          },
          workspace_id: organization?.activeWorkspace?.id,
        };

        await dispatch(updateBitbucketFiles({ body, workspace_id: organization?.activeWorkspace?.id }));
      } else {
        const docusaurusContent = jsonToDocusaurusConvertor(JSON.parse(fileData));
        const body = {
          title: newFileTitle || 'Untitled',
          icon_id: null,
          data: fileData || '# Untitled',
          markdown_data: docusaurusContent,
          meta_info: {},
          workspace_id: organization?.activeWorkspace?.id,
          folder_id: clickedDocOrFolderId,
        };
        const { payload } = await dispatch(createFile(body));
        changeNavigationOnActiveFile({ filePath: payload?.fileRes?.ref_id, fileParent: null, inEditMode: true });
      }
    },
    [organization, user, dispatch, changeNavigationOnActiveFile]
  );

  const onFileNameHandler = useCallback(
    async (templateId, name, folderId) => {
      if (!templateId) return;

      try {
        const template = organization?.activeWorkspace?.is_git_based
          ? findTemplateById(templateId, templates)
          : await getTemplateById(templateId);

        const fileData = organization?.activeWorkspace?.is_git_based ? template?.value?.markdown : template?.content;

        await newFileClickHandler(fileData, name, folderId || clickedDocOrFolderId);
        if (!organization?.activeWorkspace?.is_git_based) {
          trackTemplateUsage(templateId);
        }
      } catch (error) {
        console.error('Error handling file name:', error);
      } finally {
        dispatch(setOpenRenameFileModal(false));
        dispatch(setTemplateId(null));
        dispatch(setClickedDocOrFolderIdInStore(null));
      }
    },
    [organization, templates, dispatch, clickedDocOrFolderId, newFileClickHandler]
  );

  const createDocument = useCallback(
    (folderOrFileId) => {
      if (!templates?.length) {
        dispatch(
          fetchTemplates({
            endpoint: '/featured',
            callback: (res) => {
              if (res?.length) {
                const defaultTemplateId = res?.[0]?.id;
                onFileNameHandler(defaultTemplateId, 'New Doc', folderOrFileId);
              }
            },
          })
        );
      } else {
        const defaultTemplateId = templates?.[0]?.id;
        onFileNameHandler(defaultTemplateId, 'New Doc', folderOrFileId);
      }
    },
    [onFileNameHandler, templates]
  );

  return {
    openCreateDocument,
    onFileNameHandler,
    createDocument,
    newFileClickHandler,
  };
};

export default useDocumentCreation;
